/**
 * MSAL Token Helper
 * Helper functions to retrieve and manage MSAL tokens for API requests
 */

// Import msalInstance globally (will be set by index.js)
let msalInstanceRef = null;

export const setMsalInstance = (instance) => {
    msalInstanceRef = instance;
};

export const getMsalInstance = () => {
    return msalInstanceRef;
};

/**
 * Get MSAL ID Token using MSAL's API
 * Retrieves the token directly from the MSAL account object
 * @returns {string|null} The ID token or null if not found
 */
export const getMsalIdToken = () => {
    try {
        if (msalInstanceRef) {
            const accounts = msalInstanceRef.getAllAccounts();
            if (accounts.length > 0) {
                const account = accounts[0];
                if (account.idToken) {
                    return account.idToken;
                }
            }
        }

        return null;
    } catch (error) {
        console.error("Error retrieving MSAL token:", error);
        return null;
    }
};

/**
 * Get MSAL access token (for API calls)
 * @returns {string|null} The access token or null if not found
 */
export const getMsalAccessToken = () => {
    try {
        if (msalInstanceRef) {
            const accounts = msalInstanceRef.getAllAccounts();
            if (accounts.length > 0) {
                const account = accounts[0];
                // Access token might be in account object or needs to be acquired
                if (account.accessToken) {
                    return account.accessToken;
                }
            }
        }
        return null;
    } catch (error) {
        console.error("Error retrieving MSAL access token:", error);
        return null;
    }
};

/**
 * Silently acquire a fresh token using MSAL's refresh token.
 * Returns the new ID token on success, or null if interaction is required.
 * @param {Object} loginRequest - The scopes/request object from authConfig
 * @returns {Promise<string|null>} The fresh ID token or null
 */
export const acquireTokenSilently = async (loginRequest) => {
    try {
        if (!msalInstanceRef) return null;

        const accounts = msalInstanceRef.getAllAccounts();
        if (accounts.length === 0) return null;

        const response = await msalInstanceRef.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
            forceRefresh: true
        });

        return response?.idToken || null;
    } catch (error) {
        // InteractionRequiredAuthError means refresh token is also expired
        return null;
    }
};

/**
 * Check if user is authenticated with MSAL
 * @returns {boolean} True if MSAL account exists
 */
export const isMsalAuthenticated = () => {
    try {
        if (msalInstanceRef) {
            const accounts = msalInstanceRef.getAllAccounts();
            return accounts.length > 0;
        }
        return false;
    } catch (error) {
        return false;
    }
};
